/**
 * The mouja.ai application shell: providers composed once, and the route
 * composer. Every route is owned by a feature and registered through that
 * feature's `routes.tsx`; this file only orders them. Paths are the public
 * contract and are declared in each feature's `module.yaml`.
 *
 *   * `/` and `/business-profile` — merchant-intake (landing, intake preview).
 *   * `/signin`, `/sign-in`, `/sign-in/pending` — contributor sign-in.
 *   * The merchant workspace layout route — merchant-shell, composing the
 *     production-lines, intake, taste-calibration, recommendations, videos,
 *     business-profile and settings routes inside one shell instance.
 *   * `/onboarding` and the `/*` catch-all — the contributor workspace.
 */

import type { ReactNode } from 'react';
import { BrowserRouter, Routes } from 'react-router-dom';

import { LanguageProvider } from './i18n/language.js';
import { EnvironmentBanner } from './shell/EnvironmentBanner.js';
import { SessionProvider } from './workspace/session.js';
import { ToastProvider } from './workspace/ui.js';
import {
  contributorSignInRoutes,
  contributorWorkspaceRoutes,
} from './workspace/routes.js';
import { merchantIntakePublicRoutes } from './features/merchant-intake/routes.js';
import { merchantShellRoutes } from './features/merchant-shell/routes.js';

export function AppRoutes(): ReactNode {
  return (
    <Routes>
      {merchantIntakePublicRoutes()}
      {contributorSignInRoutes()}
      {merchantShellRoutes()}
      {contributorWorkspaceRoutes()}
    </Routes>
  );
}

export function App(): ReactNode {
  return (
    <LanguageProvider>
      <SessionProvider>
        <ToastProvider>
          {/* Outside the router: it belongs to the deployment, not to a
              page, and it must survive every navigation. */}
          <EnvironmentBanner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ToastProvider>
      </SessionProvider>
    </LanguageProvider>
  );
}
